import Cookies from "js-cookie";
import { setSegment } from "../utils/utils";
import acceptedCookiePolicy from "../components/onetrust/cookiePolicy";
import * as Sentry from "@sentry/browser";
import filterXSS from "xss";
import dataLayerFormFreeTrial from "../components_split/_imports/dataLayers/form.free-trial.common";
import dataLayerFormRoiCalculator from "../components_split/_imports/dataLayers/form.roi-calculator.common";
import dataLayerFormContactUs from "../components_split/_imports/dataLayers/form.contact.common";
import dataLayerFormBecomePartner from "../components_split/_imports/dataLayers/form.partner.common";
import dataLayerFormResources from "../components_split/_imports/dataLayers/form.resources.common";
import dataLayerFormGetDemo from "../components_split/_imports/dataLayers/form.get-a-demo.common";
import AmpLogFormSubmitted from "../components_split/_imports/amplitude/form-submitted.common";
import { updateLocalStorageMarketoFirstName } from "../components_split/forms/_imports/shared/forms-data-storage.common";

let blockCreateLead = false;
let pushDataLayers = true;

const handleErrors = (error) => {
    Sentry.captureException(error);
    throw new Error(error);    
};

/*
 * Associate anonymous lead to know lead
 */
const apiMarketoAssociateLead = (config = {}) => {
    const id = config.id || Cookies.get("marketo_id");
    const cookie = config.cookie || Cookies.get("_mkto_trk");

    http.post(`/api/sitecore/marketo/AssociateLead`, {
        id: id,
        cookie: cookie,
    })
        .then(function (response) {
            const data = response.data;

            if (data.success) {
                acceptedCookiePolicy({
                    ifFunctionalCookies: () => {
                        Cookies.set("associate_lead", "true");
                    },
                });
            }
        })
        .catch(handleErrors);
};

/*
 * Get Marketo lead by email or id
 * ======================
 * To call the endpoint, import this function and initialise it with the email or id parameter
 * ex:
 * import {apiMarketoGetLead} from './api/marketo';
 * apiMarketoGetLead({ id: '2555735' });
 * apiMarketoGetLead({ email: 'youremail@email.com' });
 */

let domain = "coveo.com";
let origin = window.location.hostname;

if (origin.indexOf("coveo.com") <= -1) {
    domain = "";
}

const apiMarketoGetLead = (config = {}) => {
    const email = config.email || "";
    const id = config.id || "";
    const form = config.form || "";
    const formData = config.formData || {};
    const success = config.success || null;
    const noResult = config.noResult || null;
    const errorCallback = config.error || null;
    const afterResponse = config.afterResponse || null;
    const requestDelay = config.requestDelay || 0;
    const fields = config.fields || [];
    const requiredFields = config.requiredFields || [];
    const noLeadCreation = config.noLeadCreation || false;

    let query = config.query || "";

    try {
        if (email) {
            query = `email=${email}`;
        }

        if (id) {
            query = `id=${id}`;
        }
    } catch (err) {
        console.log(err);
    }

    if (fields.length) {
        query += `&fields=${fields.toString()}`;
    }

    setTimeout(() => {
        http.get(`/api/sitecore/marketo/Lead?${query}`)
            .then(function (response) {
                const data = response.data;
                let needProfiling = false;

                try {
                    if (data) {
                        const [result] = data.result;

                        if (result) {
                            result.fields = data.fields || {};

                            for (let fieldWithValue of requiredFields) {
                                if (!result.fields[fieldWithValue]) {
                                    needProfiling = true;

                                    break;
                                }
                            }

                            acceptedCookiePolicy({
                                ifTargetingCookies: () => {
                                    if (!needProfiling) {
                                        Cookies.set("marketo_id", result.id, { domain: domain, expires: 7 });
                                    }

                                    updateLocalStorageMarketoFirstName(result.firstName);
                                },
                                ifNoTargetingCookies: () => {
                                    if (!needProfiling) {
                                        Cookies.set("marketo_id", "ot", { domain: domain });
                                    }

                                    if (Object.keys(formData).length) {
                                        formData.mktoID = `${result.id}`;

                                        apiUpdateLead({
                                            formSerializeString: formData,
                                        });

                                        blockCreateLead = true;
                                    }
                                },
                            });

                            if (!noLeadCreation) {
                                acceptedCookiePolicy({
                                    ifTargetingCookies: () => {
                                        if (!needProfiling) {
                                            pushDataLayers = false;

                                            apiMarketoCreateLead({
                                                data: formData,
                                                success: () => {
                                                    pushDataLayers = true;
                                                },
                                            });
                                        }
                                    },
                                });
                            }

                            if (success) {
                                success(result);
                            }
                        } else {
                            console.log("no result");

                            if (Cookies.get("marketo_id")) {
                                Cookies.remove("marketo_id", { domain: domain });
                            }

                            if (noResult) {
                                const result = {};
                                result.fields = data.fields || {};

                                noResult(result);
                            }
                        }

                        if (afterResponse) {
                            afterResponse();
                        }
                    }
                } catch (err) {
                    console.log(err);
                    Sentry.captureException(err);
                    if (errorCallback) {
                        errorCallback();
                    }
                }
            })
            .catch((e) => {
                if (errorCallback) {
                    errorCallback();
                }

                handleErrors(e);
            });
    }, requestDelay);
};

/*
 * Create Marketo lead
 * ======================
 * To call the endpoint, import this function and initialise it with the data parameter
 * The data should be a string object
 * ex:
 * import {apiMarketoCreateLead} from './api/marketo';
 * apiMarketoCreateLead({ data: '{"Language":"en","formid":"1726","Email":"npoliquin+ld0@coveo.com","Sitecore_Version__c":"82"}' });
 * window._loq - Create an anonymous tag in Lucky orange with the value mktoID_xxx based on the Marketo response
 */

const addUtmsToPayload = (param) => {
    if (param.utms && param.utms.length) {
        param.utms.map((utm) => utm.split("=")).forEach((utm) => (param.data[utm[0]] = utm[1]));
    }
};

const apiMarketoCreateLead = (config = {}) => {
    const data = config.data || {};
    const success = config.success || null;
    const error = config.error || null;
    const state = data.State;
    const formData = data;
    const isResource = document.body.dataset.template?.indexOf("resource") > -1;

    let country = data.Country;

    if (blockCreateLead && isResource) {
        return;
    }

    const mktoUtmsRaw = Cookies.get("mkto_utms") || Cookies.get("mkto_organic");
  
    const mktoCpcUtmsRaw = Cookies.get("mkto_utms_cpc");

    if (mktoUtmsRaw) {
        const mktoUtms = JSON.parse(mktoUtmsRaw);

        addUtmsToPayload({
            data,
            utms: mktoUtms,
        });

        data["utmchannel"] = "1";
    } else if (!mktoUtmsRaw && mktoCpcUtmsRaw) {
        const mktoUtmsCpc = JSON.parse(mktoCpcUtmsRaw);

        addUtmsToPayload({
            data,
            utms: mktoUtmsCpc,
        });

        data["utmchannel"] = "2";
    }

    for (let field in data) {
        field = filterXSS(data[field]);
    }

    /* TEMPORARY FIX FOR WEBM-5723 */

    if (data.mktoAssetDownloadURL) {
        if (data.mktoAssetDownloadURL.startsWith('/')) {
            data.mktoAssetDownloadURL = `${window.location.origin}${data.mktoAssetDownloadURL}`;
        } else if (data.mktoAssetDownloadURL.match(/https:\/\//g).length > 1) {
            const parts = data.mktoAssetDownloadURL.split('https://');

            data.mktoAssetDownloadURL = `https://${parts[parts.length - 1]}`;
        }
    }

    /* Convert potential wrongly formated field value as [path][to][page] instead of expected path/to/page */
    if (data['MarketoDetailIDUrl']) {
        data['MarketoDetailIDUrl'] = data['MarketoDetailIDUrl'].replaceAll('][', '/').replaceAll(']', '').replaceAll('[', '');
    }

    if(!data['SubmissionURL__c']) {
        data['SubmissionURL__c'] = window.location.origin + window.location.pathname;
    }

    if (!data['language'] && !data['Language']) {
        data['language'] = lang;
    }

    if (data['FirstName']) {
        acceptedCookiePolicy({
            ifTargetingCookies: () => {
                updateLocalStorageMarketoFirstName(data['FirstName']);
            },
        });
    }

    http.post(`/api/sitecore/marketo/CreateLeads`, JSON.stringify(data))
        .then(function (response) {
            const data = response?.data;
            const result = Array.isArray(data?.result) && data?.result.length > 0 ? data?.result[0] : null;

            try {
                if (data.error) {
                    if (error) {
                        error(data.error);
                    }

                    handleErrors(data.error);
                }

                if (result) {
                    const segment = setSegment();

                    if (!result.result || result.result.length === 0) {
                        Sentry.captureException('Marketo API returned an empty result.');
                    }

                    if(result.warnings && result.warnings.length > 0){
                        Sentry.captureException(`Marketo Warnings: ${result.warnings}`);
                    }

                    if (country) {
                        country = country.toLowerCase();

                        if (country === "united states" || country === "canada") {
                            if (!state || state === "") {
                                /*
                                 * Anonymize Email, FirstName, LastName, Phone.
                                 * We don't want to store personal data in Sentry.
                                 */

                                let email = formData.Email;
                                let emailName = email?.split("@")[0];

                                const anonymize = "****";
                                const emailDomain = email?.split("@")[1];

                                let firstname = formData.FirstName;
                                firstname = firstname?.split(firstname.slice(4)).join(anonymize);

                                let lastname = formData.LastName;
                                lastname = lastname?.split(lastname.slice(4)).join(anonymize);

                                let phone = formData.Phone;
                                phone = phone?.split(phone.slice(4)).join(anonymize);

                                emailName = email?.split(email.slice(4)).join(anonymize);
                                email = `${emailName}@${emailDomain}`;

                                formData.Email = email;
                                formData.FirstName = firstname;
                                formData.LastName = lastname;
                                
                                if (formData.Phone) {
                                    formData.Phone = phone;
                                }

                                formData.mktoID = result.id;

                                Sentry.configureScope(function (scope) {
                                    scope.setExtra("Form Fields", formData);
                                });

                                Sentry.captureException(`Marketo CreateLeads | State is missing | Marketo ID: ${result.id}`);
                            }
                        }
                    }

                    switch (result.status) {
                        case "created": //success created
                            const nonClient = "new-lead";
                            let nonClientEmail = formData.Email;
                            console.log("is created");

                            acceptedCookiePolicy({
                                ifTargetingCookies: () => {
                                    Cookies.set("marketo_id", result.id, { domain: domain, expires: 7 });
                                },
                                ifNoTargetingCookies: () => {
                                    Cookies.set("marketo_id", "ot", { domain: domain });

                                    blockCreateLead = true;
                                },
                            });

                            dataLayerFormFreeTrial(nonClient, nonClientEmail, result.id);
                            dataLayerFormContactUs(nonClient, nonClientEmail, result.id);
                            dataLayerFormBecomePartner(nonClient, nonClientEmail, result.id);
                            dataLayerFormResources(nonClient, nonClientEmail, result.id);
                            dataLayerFormGetDemo(nonClient, nonClientEmail, result.id);
                            dataLayerFormRoiCalculator(nonClient, nonClientEmail, result.id);
                            AmpLogFormSubmitted(nonClientEmail, result?.id, nonClient, formData?.Company);
                            
                            if (typeof dataLayer !== "undefined") {
                                dataLayerBus.push({
                                    eventCategory: "lead form",
                                    eventAction: `formFilled_${segment}`,
                                    eventLabel: "new lead",
                                    event: `formFilled_`,
                                });
                            }

                            if (success) {
                                success(result.status);
                            }

                            if (!Cookies.get("associate_lead") && Cookies.get("_mkto_trk")) {
                                apiMarketoAssociateLead();
                            }

                            break;
                        case "updated": //success updated
                            const existingClient = "existing-lead";
                            let existingClientEmail = formData.Email;

                            if (typeof dataLayer !== "undefined") {
                                dataLayerBus.push({
                                    eventCategory: "lead form",
                                    eventAction: `formFilled_${segment}`,
                                    eventLabel: "existing",
                                    event: `formFilled_`,
                                });
                            }

                            console.log("is updated");

                            acceptedCookiePolicy({
                                ifTargetingCookies: () => {
                                    console.log("created");
                                    Cookies.set("marketo_id", result.id, { domain: domain, expires: 7 });
                                },
                                ifNoTargetingCookies: () => {
                                    console.log("not created");
                                    Cookies.set("marketo_id", "ot", { domain: domain });

                                    blockCreateLead = true;
                                },
                            });

                            if (pushDataLayers) {
                                dataLayerFormFreeTrial(existingClient, existingClientEmail, result.id);
                                dataLayerFormContactUs(existingClient, existingClientEmail, result.id);
                                dataLayerFormBecomePartner(existingClient, existingClientEmail, result.id);
                                dataLayerFormResources(existingClient, existingClientEmail, result.id);
                                dataLayerFormGetDemo(existingClient, existingClientEmail, result.id);
                                dataLayerFormRoiCalculator(existingClient, existingClientEmail, result.id);
                                AmpLogFormSubmitted(existingClientEmail, result?.id, existingClient, formData?.Company);
                            }

                            if (success) {
                                success(result.id);
                            }

                            if (!Cookies.get("associate_lead") && Cookies.get("_mkto_trk")) {
                                apiMarketoAssociateLead();
                            }
                            break;
                        default: //unknown status/error
                            if (error) {
                                error(result.status);
                            }

                            if (response.data) {
                                if (response.data.error) {
                                    if (typeof dataLayer !== "undefined") {
                                        dataLayerBus.push({
                                            eventCategory: "lead form",
                                            eventAction: `formFilled_${segment}`,
                                            eventLabel: `error - ${response.data.error}`,
                                            event: `formFilled_`,
                                        });
                                    }
                                    
                                    console.log(response.data.error);

                                    Sentry.captureException(response.data.error);
                                }
                            }

                            if (result) {
                                if (result.reasons[0].message) {
                                    if (typeof dataLayer !== "undefined") {
                                        dataLayerBus.push({
                                            eventCategory: "lead form",
                                            eventAction: `formFilled_${segment}`,
                                            eventLabel: `error - ${response.data.error}`,
                                            event: `formFilled_`,
                                        });
                                    }

                                    console.log(result.reasons[0].message);
                                    Sentry.captureException(result.reasons[0].message);
                                }
                            }
                            break;
                    }
                }
            } catch (err) {
                throw new Error(err);
            }
        })
        .catch((e) => {
            if (error) {
                error();
                handleErrors(e);
            }            
        });
};

/**
 * Update Marketo lead with the marketo_id
 * @param config.then - initialize code if the POST is successful
 * @param config.formSerializeString - form serialize data
 */
const apiUpdateLead = (config = {}) => {
    const then = config.then || null;
    const marketoID = Cookies.get("marketo_id");
    const formSerializeString = config.formSerializeString || {};

    if (marketoID) {
        if (marketoID !== "ot") {
            formSerializeString.mktoID = marketoID;
        }

        apiMarketoCreateLead({
            data: formSerializeString,
            success: (response) => {
                if (then) {
                    then();
                }
            },
            error: (error) => {
                throw new Error(error);
            },
        });
    }
};

export { apiMarketoGetLead, apiMarketoCreateLead, apiMarketoAssociateLead, apiUpdateLead };
